import { DataProvider as DataProviderType } from "@refinedev/core";
import { DataProvider } from "@refinedev/strapi-v4";
import axios from "axios";

// Strapi API configuration
const API_URL = import.meta.env.VITE_STRAPI_URL || "https://api.yade-home.de";

// Create axios instance with Strapi configuration for Content API
export const strapiAxios = axios.create({
  baseURL: `${API_URL}/api`,
});

// Create separate axios instance for Admin API
export const strapiAdminAxios = axios.create({
  baseURL: API_URL,
});

// Add request interceptor to attach JWT token for Content API
strapiAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("strapi-jwt-token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add request interceptor to attach JWT token for Admin API
strapiAdminAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("strapi-jwt-token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle Strapi v5 response format
strapiAxios.interceptors.response.use(
  (response) => {
    // Admin endpoints have different response structure
    if (response.config.url?.includes('/admin/')) {
      // Admin responses are already in correct format {data: {...}}
      return response;
    }

    // Strapi v5 wraps responses in {data: ...}
    // For auth endpoints, unwrap to get {jwt, user}
    if (response.config.url?.includes('/auth/')) {
      return response;
    }
    return response;
  },
  (error) => {
    // Handle Strapi error format: {data: null, error: {...}}
    if (error.response?.data?.error) {
      const strapiError = error.response.data.error;
      error.message = strapiError.message || error.message;
      error.details = strapiError.details;
    }
    return Promise.reject(error);
  }
);

// Create Strapi data provider
export const strapiDataProvider: DataProviderType = DataProvider(
  `${API_URL}/api`,
  strapiAxios
);

// Custom methods for CRM-specific endpoints
export const crmApi = {
  // Lead management
  calculateLeadScore: async (leadId: number) => {
    const response = await strapiAxios.post(`/leads/${leadId}/calculate-score`);
    return response.data;
  },

  autoAssignLead: async (leadId: number) => {
    const response = await strapiAxios.post(`/leads/${leadId}/auto-assign`);
    return response.data;
  },

  getLeadStats: async () => {
    const response = await strapiAxios.get('/leads/stats');
    return response.data;
  },

  sendEmailToLead: async (leadId: number, emailData: any) => {
    const response = await strapiAxios.post(`/leads/${leadId}/send-email`, emailData);
    return response.data;
  },

  // Deal management
  getPipelineStats: async () => {
    const response = await strapiAxios.get('/deals/pipeline-stats');
    return response.data;
  },

  moveDealToStage: async (dealId: number, stage: string, notes?: string) => {
    const response = await strapiAxios.post(`/deals/${dealId}/move-to-stage`, {
      stage,
      notes
    });
    return response.data;
  },

  getStuckDeals: async (days: number = 30) => {
    const response = await strapiAxios.get(`/deals/stuck?days=${days}`);
    return response.data;
  },

  // Viewing management
  getUpcomingViewings: async (days: number = 7) => {
    const response = await strapiAxios.get(`/viewings/upcoming?days=${days}`);
    return response.data;
  },

  confirmViewing: async (viewingId: number) => {
    const response = await strapiAxios.post(`/viewings/${viewingId}/confirm`);
    return response.data;
  },

  completeViewing: async (viewingId: number, data: {
    leadInterestRating?: number;
    viewingNotes?: string;
    nextSteps?: string;
    followUpRequired?: boolean;
  }) => {
    const response = await strapiAxios.post(`/viewings/${viewingId}/complete`, data);
    return response.data;
  },

  cancelViewing: async (viewingId: number, reason?: string) => {
    const response = await strapiAxios.post(`/viewings/${viewingId}/cancel`, {
      reason
    });
    return response.data;
  },

  // Task management
  getMyTasks: async (status?: string, priority?: string) => {
    let url = '/tasks/my-tasks';
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (priority) params.append('priority', priority);
    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;

    const response = await strapiAxios.get(url);
    return response.data;
  },

  getOverdueTasks: async () => {
    const response = await strapiAxios.get('/tasks/overdue');
    return response.data;
  },

  completeTask: async (taskId: number, outcome?: string, timeSpentMinutes?: number) => {
    const response = await strapiAxios.post(`/tasks/${taskId}/complete`, {
      outcome,
      timeSpentMinutes
    });
    return response.data;
  },

  getTaskStats: async () => {
    const response = await strapiAxios.get('/tasks/stats');
    return response.data;
  },

  // Document management
  getDocumentsByLead: async (leadId: number) => {
    const response = await strapiAxios.get(`/documents/by-lead/${leadId}`);
    return response.data;
  },

  verifyDocument: async (documentId: number) => {
    const response = await strapiAxios.post(`/documents/${documentId}/verify`);
    return response.data;
  },

  getExpiringDocuments: async (days: number = 30) => {
    const response = await strapiAxios.get(`/documents/expiring?days=${days}`);
    return response.data;
  },

  markDocumentExpired: async (documentId: number) => {
    const response = await strapiAxios.post(`/documents/${documentId}/mark-expired`);
    return response.data;
  },

  gdprDeleteDocument: async (documentId: number) => {
    const response = await strapiAxios.delete(`/documents/${documentId}/gdpr-delete`);
    return response.data;
  },

  // Activity management
  getRecentActivities: async (params?: {
    limit?: number;
    leadId?: string;
    type?: 'email' | 'call';
    startDate?: string;
    endDate?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.leadId) queryParams.append('leadId', params.leadId);
    if (params?.type) queryParams.append('type', params.type);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const url = `/activities/recent${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await strapiAxios.get(url);
    return response.data;
  },

  getActivityStats: async (params?: {
    leadId?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.leadId) queryParams.append('leadId', params.leadId);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const url = `/activities/stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await strapiAxios.get(url);
    return response.data;
  },

  getActivitiesByLead: async (leadId: string, limit = 100) => {
    const response = await strapiAxios.get(`/activities/by-lead/${leadId}?limit=${limit}`);
    return response.data;
  }
};

export { API_URL };
